import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/base/Seo";
import CallToAction from "@/pages/home/components/CallToAction";
import DonationPanel from "./components/DonationPanel";
import { supabase } from "@/lib/supabase";
import { formatEuro } from "@/lib/format";
import type { Action } from "@/lib/types";

export default function ActionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [action, setAction] = useState<Action | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [donationBanner, setDonationBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("actions")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (err) throw err;
      setAction((data ?? null) as Action | null);
    } catch {
      setError("Impossible de charger cette action.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) load();
  }, [load]);

  useEffect(() => {
    const donation = searchParams.get("donation");
    const sessionId = searchParams.get("session_id");
    if (donation === "cancel") {
      setDonationBanner({
        type: "error",
        message: "Paiement annulé. Vous pouvez réessayer à tout moment.",
      });
      return;
    }
    if (donation === "success" && sessionId) {
      (async () => {
        try {
          const { data, error: err } = await supabase.functions.invoke(
            "donate-stripe-confirm",
            { body: { sessionId } }
          );
          if (err || data?.status !== "paid") throw new Error("not paid");
          setDonationBanner({
            type: "success",
            message:
              "Merci pour votre générosité ! Votre don a bien été enregistré.",
          });
          load();
        } catch {
          setDonationBanner({
            type: "error",
            message:
              "Nous n'avons pas pu confirmer votre don. Contactez-nous pour vérifier.",
          });
        }
      })();
    }
  }, [searchParams, load]);

  const gallery = action?.gallery && action.gallery.length > 0 ? action.gallery : [];
  const heroImage = gallery[0] ?? "";
  const goal = Number(action?.cagnotte_goal || 0);
  const collected = Number(action?.cagnotte_collected || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((collected / goal) * 100)) : 0;
  const hasCagnotte = goal > 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={action ? `${action.title} | Humanity59` : "Action | Humanity59"}
        description={
          action
            ? action.description
            : "Découvrez cette action humanitaire menée par Humanity59."
        }
        keywords="action humanitaire, solidarité, don, Humanity59"
        path={`/actions/${slug ?? ""}`}
        ogType="article"
      />
      <Header />
      <main className="flex-1">
        {loading ? (
          <div className="py-40 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-primary-500"></i>
          </div>
        ) : error || !action ? (
          <div className="py-40 text-center px-4">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
              Action introuvable
            </h1>
            <p className="mt-2 text-foreground-600">
              Cette action n'existe pas ou n'est plus disponible.
            </p>
            <Link
              to="/actions"
              className="mt-8 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour aux actions
            </Link>
          </div>
        ) : (
          <>
            <section className="relative h-[360px] md:h-[480px] flex items-end overflow-hidden">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={action.title}
                  title={`${action.title} — Humanity59`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
              <div className="relative z-10 w-full px-4 md:px-6 pb-10 md:pb-14">
                <div className="max-w-4xl mx-auto">
                  <Link
                    to="/actions"
                    className="inline-flex items-center gap-2 text-sm font-medium text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Nos actions
                  </Link>
                  <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
                    {action.title}
                  </h1>
                </div>
              </div>
            </section>

            {donationBanner && (
              <div className="max-w-4xl mx-auto px-4 md:px-6 mt-6">
                <div
                  className={`rounded-md px-4 py-3 text-sm ${
                    donationBanner.type === "success"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-accent-100 text-accent-700"
                  }`}
                >
                  <i
                    className={`${
                      donationBanner.type === "success"
                        ? "ri-checkbox-circle-line"
                        : "ri-error-warning-line"
                    } mr-2`}
                  ></i>
                  {donationBanner.message}
                </div>
              </div>
            )}

            <section className="py-16 md:py-24 bg-background-50">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-6 p-6 md:p-8 bg-background-50 rounded-lg border border-background-200">
                  <span className="w-16 h-16 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${action.icon} text-3xl`}></i>
                  </span>
                  <div className="flex-1">
                    <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">
                      {action.stat_value}
                    </p>
                    <p className="mt-1 text-sm text-foreground-500">
                      {action.stat_label}
                    </p>
                  </div>
                </div>

                {hasCagnotte && (
                  <div className="mt-6 p-6 md:p-8 bg-background-50 rounded-lg border border-background-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-primary-600">
                        {formatEuro(collected)} collectés
                      </span>
                      <span className="font-heading text-lg font-bold text-foreground-950">
                        {pct}%
                      </span>
                    </div>
                    <div className="mt-3 h-3 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-700"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="mt-2 text-sm text-foreground-500">
                      Objectif : {formatEuro(goal)}
                    </p>
                  </div>
                )}

                <div className="mt-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    À propos de cette action
                  </h2>
                  <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed whitespace-pre-line">
                    {action.description}
                  </p>
                </div>

                {hasCagnotte && (
                  <div className="mt-12">
                    <DonationPanel actionId={action.id} slug={action.slug} />
                  </div>
                )}

                {gallery.length > 1 && (
                  <div className="mt-12">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                      En images
                    </h2>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {gallery.map((img, idx) => (
                        <div
                          key={img}
                          className="w-full aspect-[4/3] overflow-hidden rounded-lg border border-background-200"
                        >
                          <img
                            src={img}
                            alt={`${action.title} — photo ${idx + 1}`}
                            title={`${action.title} — photo ${idx + 1}`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        <CallToAction />
      </main>
      <Footer />
    </div>
  );
}